/**
 * Helpers for inserting and removing decorative inline SVGs in section HTML.
 * The library picker reads SVG files, but stored section state keeps inline SVG
 * so preview and export do not depend on builder-local asset URLs.
 */

import { reservedEditableIds } from '../shared/editable-id-repair.js';
import { injectEditableAttrs } from './editable-utils.js';
import { serializeSvg } from './svg-sanitizer.js';
import {
  prepareDecorativeSvg,
  applyDecorativeColor,
  hasRecordedPaint,
  hasUnrecordedFlatten,
  DECORATIVE_COLOR_TOKENS
} from './decorative-transform.js';
import { DECORATIVE_CLASSES, DECORATIVE_SELECTOR } from '../shared/element-kind.js';

export { DECORATIVE_COLOR_TOKENS, DECORATIVE_CLASSES, DECORATIVE_SELECTOR };

function parseHTMLFragment(html) {
  if (typeof DOMParser === 'undefined') {
    throw new Error('DOMParser is required to edit decorative SVGs');
  }
  const doc = new DOMParser().parseFromString(`<div>${html || ''}</div>`, 'text/html');
  return { doc, root: doc.body.firstElementChild };
}

function ensureClass(el, className) {
  const classes = new Set((el.getAttribute('class') || '').split(/\s+/).filter(Boolean));
  classes.add(className);
  el.setAttribute('class', Array.from(classes).join(' '));
}

/**
 * DS-2: the paint transform moved to `decorative-transform.js`, so the preview
 * and the insert run the SAME code rather than a model of each other, and so
 * the flatten became reversible. `shouldRewriteSvgPaint`,
 * `makeSvgPaintThemeable` and `applyDecorativeColor` all live there now.
 */

/** The prefix that makes an inlined SVG's ids unique on the page. */
function makeIdPrefix() {
  return `pb-decorative-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function nextEditableId(root, editableIdRepairs) {
  const reserved = reservedEditableIds(editableIdRepairs);
  let counter = 0;
  while (reserved.has(`e${++counter}`) || root.querySelector(`[data-pb-eid="e${counter}"]`)) {
    // The condition advances until this section has an unreserved identity.
  }
  return `e${counter}`;
}

function sectionScope(section) {
  const id = section?.sectionId || section?.id || '';
  return String(id).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function makeDefaultCss(section) {
  const scope = sectionScope(section);
  if (!scope) return '';
  return `[data-pb-section="${scope}"] {
  position: relative;
  overflow: hidden;
  isolation: isolate;
}

[data-pb-section="${scope}"] .pb-decorative-svg {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: none;
  min-width: 0;
  height: auto;
  display: block;
  transform: none;
  object-fit: fill;
  z-index: 0;
  pointer-events: none;
}

[data-pb-section="${scope}"] .pb-decorative-svg[data-pb-decorative="true"] {
  pointer-events: auto;
}`;
}

/**
 * DS-6: the top-edge rules, kept SEPARATE from the block above.
 *
 * A section that already carries the base block gets only this appended (see
 * `missingDecorativeCss`), so an existing project gains top-edge support
 * without its authored block being rewritten.
 *
 * `bottom: auto` is not optional: the base rule sets `bottom: 0`, and a rule
 * that only added `top: 0` would leave BOTH set, stretching the shape over the
 * section's whole height.
 */
function makeTopEdgeCss(section) {
  const scope = sectionScope(section);
  if (!scope) return '';
  return `[data-pb-section="${scope}"] .pb-decorative-svg.pb-decorative-top {
  top: 0;
  bottom: auto;
}

[data-pb-section="${scope}"] .pb-decorative-svg.pb-decorative-flip {
  transform: scaleY(-1);
}

[data-pb-section="${scope}"] .pb-decorative-svg.pb-decorative-top.pb-decorative-flip {
  transform: scaleY(-1);
}`;
}

/**
 * What this section's CSS is MISSING, not whether it has any.
 *
 * `hasDecorativeCss` tested `/\bpb-decorative-svg\b/` — true of every section
 * that has ever had a decorative — so a project predating DS-6 would never
 * receive the top-edge rules. The shape would be inserted, classed
 * `pb-decorative-top`, and pinned to the BOTTOM anyway, which is the version
 * trap the plan names.
 *
 * Detection is per SELECTOR, and the base block is never rewritten: someone may
 * have edited it, and a section must not be left holding half the CSS.
 */
function missingDecorativeCss(section) {
  const css = section?.css || '';
  const parts = [];
  if (!/\bpb-decorative-svg\b/.test(css)) parts.push(makeDefaultCss(section));
  if (!/\bpb-decorative-top\b/.test(css)) parts.push(makeTopEdgeCss(section));
  return parts.filter(Boolean).join('\n\n') || null;
}

function decorativeName(el, index) {
  const label = el.getAttribute('data-pb-decorative-name');
  if (label) return label;
  const src = el.getAttribute('src') || '';
  if (src) {
    const name = src.split(/[/?#]/).filter(Boolean).pop();
    if (name) return decodeURIComponent(name).replace(/\.svg$/i, '');
  }
  return `Decorative ${index + 1}`;
}

export function listDecorativeSvgs(sectionHtml) {
  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return [];
  injectEditableAttrs(root);
  const editables = Array.from(root.querySelectorAll('[data-pb-editable]'));
  return Array.from(root.querySelectorAll(DECORATIVE_SELECTOR))
    .filter((el) => el.tagName.toLowerCase() === 'svg' || el.tagName.toLowerCase() === 'img')
    .map((el, index) => ({
      index,
      editableIndex: editables.indexOf(el),
      editableType: el.getAttribute('data-pb-editable') || '',
      eid: el.getAttribute('data-pb-eid') || '',
      tagName: el.tagName.toLowerCase(),
      name: decorativeName(el, index),
      src: el.getAttribute('src') || '',
      className: el.getAttribute('class') || '',
      colorToken: el.getAttribute('data-pb-decorative-color') || '',
      markup: el.outerHTML
    }));
}

/**
 * Insert a decorative SVG into a section.
 *
 * DS-2: `colorToken` DEFAULTS TO `'original'`. It was hardcoded `'--primary'`,
 * so every insert flattened the artwork to one colour — while the library that
 * chose it showed, sorted and named by the colour being destroyed.
 */
export function insertDecorativeSvg(section, svgMarkup, indexEntry = {}, opts = {}) {
  const edge = opts.edge === 'top' ? 'top' : 'bottom';
  const { doc, root } = parseHTMLFragment(section?.html || '');
  if (!root) return { updatedHtml: null, addedScopedCss: null };

  const svg = prepareDecorativeSvg(svgMarkup, {
    colorToken: opts.colorToken || 'original',
    // Generated HERE and injected, not inside the transform: that is what makes
    // "the preview equals the insert" a byte comparison rather than a
    // substring check (see decorative-transform.js's header).
    idPrefix: makeIdPrefix(),
    eid: nextEditableId(root, section?.editableIdRepairs),
    name: indexEntry.name || 'Decorative SVG',
    filename: indexEntry.filename || ''
  });
  if (!svg) return { updatedHtml: null, addedScopedCss: null };

  ensureClass(svg, 'pb-decorative-svg');
  // DS-6 / G7: placement and flip are CLASSES, never `data-pb-*`.
  // `stripAllBuilderAttrs` removes every `data-pb-*` on export except an
  // exact-name carve-out, so an attribute-keyed selector would work on the
  // canvas and SILENTLY DIE in the shipped site. Bottom stays the unclassed
  // default, so no existing project moves and no migration is needed.
  if (edge === 'top') ensureClass(svg, 'pb-decorative-top');
  if (opts.flip) ensureClass(svg, 'pb-decorative-flip');

  const imported = doc.importNode(svg, true);
  // A TOP-edge shape is PREPENDED, not merely repositioned in CSS. Among
  // absolutely-positioned siblings, DOM order is paint order — appending a top
  // shape would stack it over a bottom one for reasons no user could see.
  if (edge === 'top') root.insertBefore(imported, root.firstChild);
  else root.appendChild(imported);

  return { updatedHtml: root.innerHTML, addedScopedCss: missingDecorativeCss(section) };
}

export function replaceDecorativeSvg(sectionHtml, decorativeIndex, svgMarkup, indexEntry = {}) {
  const { doc, root } = parseHTMLFragment(sectionHtml);
  if (!root) return null;
  const candidates = Array.from(root.querySelectorAll(DECORATIVE_SELECTOR)).filter(
    (el) => el.tagName.toLowerCase() === 'svg' || el.tagName.toLowerCase() === 'img'
  );
  const target = candidates[decorativeIndex];
  if (!target) return null;

  // Replace INHERITS the target's token, as it always has — swapping the shape
  // is not a decision about its colour. The fallback moves '--primary' →
  // 'original' with the rest of DS-2, so a replace into an untokened target no
  // longer silently flattens it.
  const svg = prepareDecorativeSvg(svgMarkup, {
    colorToken: target.getAttribute('data-pb-decorative-color') || 'original',
    idPrefix: makeIdPrefix(),
    eid: target.getAttribute('data-pb-eid') || '',
    name: indexEntry.name || decorativeName(target, decorativeIndex),
    filename: indexEntry.filename || ''
  });
  if (!svg) return null;
  ensureClass(svg, 'pb-decorative-svg');
  // Replacement changes the artwork, not its placement. Template decoratives
  // often rely on a source class plus inline top/bottom offsets; dropping
  // either makes the shared writer visually differ by entrance.
  for (const className of target.classList) ensureClass(svg, className);
  const targetStyle = target.getAttribute('style') || '';
  if (targetStyle) {
    const incomingStyle = svg.getAttribute('style') || '';
    svg.setAttribute('style', [incomingStyle, targetStyle].filter(Boolean).join(';'));
  }

  target.parentNode.replaceChild(doc.importNode(svg, true), target);
  return root.innerHTML;
}

/**
 * Set a decorative SVG's colour token.
 *
 * DS-2: THIS RETURNS A STRUCTURED RESULT, and the reason is migration honesty.
 * It used to return updated HTML or `null`, in which shape "this decorative
 * predates recorded paint" was indistinguishable from "the index was out of
 * range" and from "that element is not an svg" — three different things, one
 * silent answer, and the panel rendered a control that did nothing.
 *
 * The migration case is real and unrecoverable: a decorative inserted before
 * DS-2 carries `data-pb-decorative-color="--primary"` and NO recorded paint,
 * because the original was destroyed at insert time and never written down.
 * For those, `'original'` must fail out loud. A UI slot that makes it easy to
 * state a safety fact makes it equally easy to state a false one.
 *
 * @returns {{ok: boolean, html: string|null,
 *            reason?: 'not-found'|'not-svg'|'no-recorded-paint'}}
 */
export function setDecorativeSvgColor(sectionHtml, decorativeIndex, colorToken) {
  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return { ok: false, html: null, reason: 'not-found' };
  const candidates = Array.from(root.querySelectorAll(DECORATIVE_SELECTOR)).filter(
    (el) => el.tagName.toLowerCase() === 'svg' || el.tagName.toLowerCase() === 'img'
  );
  const target = candidates[decorativeIndex];
  if (!target) return { ok: false, html: null, reason: 'not-found' };
  if (target.tagName.toLowerCase() !== 'svg') return { ok: false, html: null, reason: 'not-svg' };

  const result = applyDecorativeColor(target, colorToken);
  if (!result.ok) return { ok: false, html: null, reason: result.reason };
  return { ok: true, html: root.innerHTML };
}

/**
 * Can this decorative be returned to its own colours?
 *
 * False for anything inserted before DS-2 — the source paint is gone. The panel
 * asks BEFORE offering Original, so the control is absent-and-explained rather
 * than present-and-dead.
 */
export function canRestoreDecorativeOriginal(sectionHtml, decorativeIndex) {
  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return false;
  const candidates = Array.from(root.querySelectorAll(DECORATIVE_SELECTOR)).filter(
    (el) => el.tagName.toLowerCase() === 'svg' || el.tagName.toLowerCase() === 'img'
  );
  const target = candidates[decorativeIndex];
  if (!target || target.tagName.toLowerCase() !== 'svg') return false;
  if (hasRecordedPaint(target)) return true;
  // No memo has two causes, and only one of them is a loss — a shape with
  // nothing rewritable to begin with is trivially already original.
  return !hasUnrecordedFlatten(target);
}

export function removeDecorativeSvg(sectionHtml, decorativeIndex) {
  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return null;
  const candidates = Array.from(root.querySelectorAll(DECORATIVE_SELECTOR)).filter(
    (el) => el.tagName.toLowerCase() === 'svg' || el.tagName.toLowerCase() === 'img'
  );
  const target = candidates[decorativeIndex];
  if (!target) return null;
  target.remove();
  return root.innerHTML;
}

export function removeDecorativeByEid(sectionHtml, eid) {
  if (!eid) return null;
  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return null;
  const escaped = String(eid).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const target = root.querySelector(`[data-pb-eid="${escaped}"]`);
  if (!target) return null;
  if (!target.matches(DECORATIVE_SELECTOR)) return null;
  const tag = target.tagName.toLowerCase();
  if (tag !== 'svg' && tag !== 'img') return null;
  target.remove();
  return root.innerHTML;
}

/**
 * Enumerate every decorative action target in a section's HTML in the same
 * descriptor shape the iframe-side resolver emits. Used by the section
 * context menu to surface "Edit background image" / "Replace divider"
 * entries without duplicating classification logic.
 *
 * Background entries are keyed off `picture.cs-background`, so they remain
 * stable after src/srcset are cleared. Divider entries are filtered to
 * <svg>/<img> matches of DECORATIVE_SELECTOR.
 */
export function listDecorativeActionTargets(sectionHtml, { instanceId = '', sectionId = '' } = {}) {
  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return [];
  injectEditableAttrs(root);

  const editables = Array.from(root.querySelectorAll('[data-pb-editable]'));
  const decoCandidates = Array.from(root.querySelectorAll(DECORATIVE_SELECTOR)).filter(
    (el) => el.tagName.toLowerCase() === 'svg' || el.tagName.toLowerCase() === 'img'
  );

  const targets = [];

  // Background pictures: the <picture> is the stable slot; child <img>
  // supplies eid/editableIndex/editableType when present.
  Array.from(root.querySelectorAll('picture.cs-background')).forEach((picture) => {
    const img = picture.querySelector('img');
    const eidHost = picture.hasAttribute('data-pb-eid')
      ? picture
      : img && img.hasAttribute('data-pb-eid')
        ? img
        : null;
    const editableHost = picture.hasAttribute('data-pb-editable')
      ? picture
      : img && img.hasAttribute('data-pb-editable')
        ? img
        : null;
    targets.push({
      owner: 'section',
      targetKind: 'decorative-background',
      capabilities: ['replace', 'remove', 'open-library'],
      instanceId,
      sectionId,
      eid: eidHost ? eidHost.getAttribute('data-pb-eid') || '' : '',
      targetRef: {
        domain: 'dom',
        eid: eidHost ? eidHost.getAttribute('data-pb-eid') || '' : ''
      },
      editableIndex: editableHost ? editables.indexOf(editableHost) : -1,
      decorativeIndex: null,
      editableType: editableHost ? editableHost.getAttribute('data-pb-editable') || '' : '',
      kind: 'background',
      decorativeName: img ? img.getAttribute('alt') || '' : '',
      tagName: 'picture',
      pictureSlot: true
    });
  });

  // Divider/decorative SVGs and decorative IMGs (waves, masks, etc.).
  decoCandidates.forEach((el, decorativeIndex) => {
    if (el.closest && el.closest('picture.cs-background')) return; // bg already covered
    targets.push({
      owner: 'section',
      targetKind: 'decorative-divider',
      capabilities: ['replace', 'remove', 'open-library'],
      instanceId,
      sectionId,
      eid: el.getAttribute('data-pb-eid') || '',
      targetRef: { domain: 'dom', eid: el.getAttribute('data-pb-eid') || '' },
      editableIndex: editables.indexOf(el),
      decorativeIndex,
      editableType: el.getAttribute('data-pb-editable') || '',
      kind: 'decoration',
      decorativeName: decorativeName(el, decorativeIndex),
      tagName: el.tagName.toLowerCase()
    });
  });

  return targets;
}

/**
 * A preview of what a decorative WILL look like once inserted.
 *
 * DS-2/DS-3: this delegates to `prepareDecorativeSvg` so it cannot drift from
 * the action. `decorative: false` skips the canvas bookkeeping (the
 * `data-pb-decorative*` markers and the eid) — a preview is a picture, not a
 * canvas element — while the PAINT transform, which is the thing the user is
 * being shown, is the identical code path.
 *
 * `idPrefix` is caller-supplied and defaults to empty, which leaves ids
 * untouched: a preview rendered once into a detached node has nothing to
 * collide with, and an empty prefix keeps the output a pure function of the
 * markup and the token.
 *
 * @param {string} markup
 * @param {{colorToken?: string, idPrefix?: string}} [opts]
 */
export function serializeDecorativePreview(markup, opts = {}) {
  const svg = prepareDecorativeSvg(markup, {
    colorToken: opts.colorToken || 'original',
    idPrefix: opts.idPrefix || '',
    decorative: false
  });
  if (!svg) return '';
  svg.removeAttribute('id');
  svg.removeAttribute('data-pb-editable');
  return serializeSvg(svg);
}
